import React, { createContext, useContext, useState, ReactNode, Dispatch, SetStateAction } from 'react';

interface TableFilterContextType {
  // Active table state
  activeTableIds: string[];
  setActiveTableIds: Dispatch<SetStateAction<string[]>>;
  
  // Filter states
  hasActiveFilters: boolean;
  setHasActiveFilters: (hasFilters: boolean) => void;
  
  // Filter data
  filterData: Record<string, any>;
  setFilterData: Dispatch<SetStateAction<Record<string, any>>>;
  
  // Helper methods
  isTableSelectionEnabled: (tableId?: string) => boolean;
  clearAllFilters: () => void;
}

const TableFilterContext = createContext<TableFilterContextType | undefined>(undefined);

interface TableFilterProviderProps {
  children: ReactNode;
}

export const TableFilterProvider: React.FC<TableFilterProviderProps> = ({ children }) => {
  const [activeTableIds, setActiveTableIds] = useState<string[]>([]);
  const [hasActiveFilters, setHasActiveFilters] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any>>({});

  // Allow multiple tables to apply filters sequentially 
  const isTableSelectionEnabled = (): boolean => {
    return true;
  };

  // Clear all filters and reset state
  const clearAllFilters = () => {
    setActiveTableIds([]);
    setHasActiveFilters(false);
    setFilterData({});
  };

  const contextValue: TableFilterContextType = {
    activeTableIds,
    setActiveTableIds,
    hasActiveFilters,
    setHasActiveFilters,
    filterData,
    setFilterData,
    isTableSelectionEnabled,
    clearAllFilters,
  };

  return (
    <TableFilterContext.Provider value={contextValue}>
      {children}
    </TableFilterContext.Provider>
  );
};

export const useTableFilter = (): TableFilterContextType => {
  const context = useContext(TableFilterContext);
  if (!context) {
    throw new Error('useTableFilter must be used within a TableFilterProvider');
  }
  return context;
};

// Custom hook for individual tables to manage their filter state
export const useTableInstance = (tableId: string) => {
  const {
    activeTableIds,
    setActiveTableIds,
    setHasActiveFilters,
    filterData,
    setFilterData,
    isTableSelectionEnabled,
  } = useTableFilter();

  const isActive = activeTableIds.includes(tableId);
  const canSelect = isTableSelectionEnabled(tableId);

  // Activate this table as the filtered table
  const activateTable = (filters?: Record<string, any>) => {
    setActiveTableIds((prev) => [...new Set([...prev, tableId])]);
    setHasActiveFilters(true);
    if (filters) {
      setFilterData((prev) => ({ ...prev, [tableId]: filters }));
    }
  };

  // Deactivate this table
  const deactivateTable = () => {
    if (activeTableIds.includes(tableId)) {
      setActiveTableIds((prev) => {
        const nextIds = prev.filter((id) => id !== tableId);
        if (nextIds.length === 0) setHasActiveFilters(false);
        return nextIds;
      });
      
      setFilterData((prev) => {
        const newFilterData = { ...prev };
        delete newFilterData[tableId];
        return newFilterData;
      });
    }
  };

  // Update filters for this table
  const updateTableFilters = (filters: Record<string, any>) => {
    setFilterData((prev) => ({ ...prev, [tableId]: filters }));
    if (Object.keys(filters).length > 0) {
      setActiveTableIds((prev) => [...new Set([...prev, tableId])]);
      setHasActiveFilters(true);
    } else {
      deactivateTable();
    }
  };

  return {
    isActive,
    canSelect,
    activateTable,
    deactivateTable,
    updateTableFilters,
    tableFilters: filterData[tableId] || {},
  };
}; 